import { persistentAtom } from '@nanostores/persistent';
import { atom } from 'nanostores';

import { type Asset } from '@/services/db/asset/entity';

import {
  type ActiveModalState,
  type AssetLibraryModalState,
  type FolderPath,
  type SearchState,
  type SortState,
  type ViewMode,
} from './types';

export type {
  ActiveModalState,
  AssetLibraryModalState,
  FolderPath,
  SearchState,
  SortField,
  SortState,
  ViewMode,
} from './types';

export const TOP_LEVEL_FOLDER_PATH: FolderPath = {
  name: 'All files',
  id: null,
} as const;

export const $viewMode = persistentAtom<ViewMode>('asset-library-view-mode', 'grid', {
  encode: JSON.stringify,
  decode: JSON.parse,
});

export const $sort = persistentAtom<SortState>(
  'asset-library-sort',
  { field: 'created_at', ascending: false },
  { encode: JSON.stringify, decode: JSON.parse },
);

export const $assetLibraryModal = atom<AssetLibraryModalState>({
  isOpen: false,
  callback: null,
  options: null,
});

// -- Local browser state (useReducer inside AssetLibraryModal) --

export type BrowserState = {
  search: SearchState;
  isFilteringByBytesEnabled: boolean;
  pageNumber: number;
  selectedAssetIds: Set<string>;
  parents: Asset[];
  activeModal: ActiveModalState;
};

export type BrowserAction =
  | { type: 'SET_SEARCH_INPUT'; input: string }
  | { type: 'SET_SEARCH_QUERY'; query: string }
  | { type: 'SET_PAGE_NUMBER'; page: number }
  | { type: 'SET_FILTERING_BY_BYTES'; enabled: boolean }
  | { type: 'TOGGLE_ASSET_SELECTION'; checked: boolean; assetId: string }
  | { type: 'CLEAR_SELECTION' }
  | { type: 'SELECT_ALL'; assets: Asset[] }
  | { type: 'SET_ASSETS_TO_DELETE'; assets: Asset[] }
  | { type: 'SHOW_CREATE_FOLDER_MODAL'; show: boolean }
  | { type: 'SET_PARENTS'; parents: Asset[] }
  | { type: 'RESET' };

export const initialBrowserState: BrowserState = {
  search: { input: '', query: '' },
  isFilteringByBytesEnabled: true,
  pageNumber: 1,
  selectedAssetIds: new Set(),
  parents: [],
  activeModal: { type: null, assetsToDelete: [] },
};

export function browserReducer(state: BrowserState, action: BrowserAction): BrowserState {
  switch (action.type) {
    case 'SET_SEARCH_INPUT':
      return { ...state, search: { ...state.search, input: action.input } };
    case 'SET_SEARCH_QUERY':
      return { ...state, search: { ...state.search, query: action.query } };
    case 'SET_PAGE_NUMBER':
      return { ...state, pageNumber: action.page };
    case 'SET_FILTERING_BY_BYTES':
      return { ...state, isFilteringByBytesEnabled: action.enabled };
    case 'TOGGLE_ASSET_SELECTION': {
      const selected = new Set(state.selectedAssetIds);
      if (action.checked) {
        selected.add(action.assetId);
      } else {
        selected.delete(action.assetId);
      }
      return { ...state, selectedAssetIds: selected };
    }
    case 'CLEAR_SELECTION':
      return { ...state, selectedAssetIds: new Set() };
    case 'SELECT_ALL':
      return { ...state, selectedAssetIds: new Set(action.assets.map((a) => a.id)) };
    case 'SET_ASSETS_TO_DELETE':
      return {
        ...state,
        activeModal: {
          type: action.assets.length > 0 ? 'deleteAssets' : null,
          assetsToDelete: action.assets,
        },
      };
    case 'SHOW_CREATE_FOLDER_MODAL':
      return {
        ...state,
        activeModal: {
          type: action.show ? 'createFolder' : null,
          assetsToDelete: [],
        },
      };
    case 'SET_PARENTS':
      return { ...state, parents: action.parents };
    case 'RESET':
      return initialBrowserState;
    default: {
      const _exhaustive: never = action;
      return _exhaustive;
    }
  }
}
